import { useState } from 'react';
import { X, Trash2, Eye, CheckCircle, Plus, Edit3 } from 'lucide-react';
import { formatPrice, cn } from '../lib/utils';
import { getEditionFullName } from '../lib/utils';
import { Sheet, SheetContent } from './ui/sheet';
import { Dialog, DialogContent, DialogTitle } from './ui/dialog';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import type { CartSession, Mode } from '../lib/types';

const CONDITION_LABELS: Record<string, string> = {
  nm: 'NM',
  lp: 'LP',
  mp: 'MP',
  hp: 'HP',
  dm: 'DMG',
};

const CONDITION_BADGE_CLASS: Record<string, string> = {
  nm: 'badge-nm',
  lp: 'badge-lp',
  mp: 'badge-mp',
  hp: 'badge-hp',
  dm: 'badge-dm',
};

interface CartDrawerProps {
  session: CartSession;
  mode: Mode;
  onClose: () => void;
  onRemoveItem: (itemId: string) => void;
  onUpdatePrice: (itemId: string, newPrice: number) => void;
  onShowTotal: () => void;
  onCompleteSession: () => void;
  onClearCart: () => void;
  parkedSessions: CartSession[];
  onSwitchSession: (sessionId: string) => void;
  onParkSession: () => void;
  onStartNewSession: (mode: Mode) => void;
  onRenameSession: (sessionId: string, label: string) => void;
  variant: 'phone' | 'tablet';
}

export function CartDrawer({
  session,
  mode,
  onClose,
  onRemoveItem,
  onUpdatePrice,
  onShowTotal,
  onCompleteSession,
  onClearCart,
  parkedSessions,
  onSwitchSession,
  onParkSession,
  onStartNewSession,
  onRenameSession,
  variant,
}: CartDrawerProps) {
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [editingPrice, setEditingPrice] = useState('');
  const [renamingSessionId, setRenamingSessionId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');

  const total = session.items.reduce((sum, item) => sum + item.finalPrice, 0);
  const totalSessions = 1 + parkedSessions.length;
  const canAddSession = totalSessions < 4;

  const startEditing = (itemId: string, currentPrice: number) => {
    setEditingItemId(itemId);
    setEditingPrice(currentPrice.toFixed(2));
  };

  const commitEdit = () => {
    if (editingItemId) {
      const newPrice = parseFloat(editingPrice);
      if (!isNaN(newPrice) && newPrice >= 0) {
        onUpdatePrice(editingItemId, newPrice);
      }
      setEditingItemId(null);
    }
  };

  const adjustPrice = (itemId: string, currentPrice: number, delta: number) => {
    const newPrice = Math.max(0, currentPrice + delta);
    onUpdatePrice(itemId, newPrice);
    if (editingItemId === itemId) {
      setEditingPrice(newPrice.toFixed(2));
    }
  };

  const startRenaming = (sessionId: string, currentLabel?: string) => {
    setRenamingSessionId(sessionId);
    setRenameValue(currentLabel || '');
  };

  const commitRename = () => {
    if (renamingSessionId) {
      onRenameSession(renamingSessionId, renameValue.trim());
      setRenamingSessionId(null);
    }
  };

  const isPhone = variant === 'phone';

  // Shared between the phone (Sheet) and tablet (Dialog) wrappers below —
  // both primitives share the same underlying @radix-ui/react-dialog Root,
  // so DialogTitle satisfies Radix's accessible-name requirement in either.
  // showCloseButton is off on both wrappers since this header carries its
  // own close button as part of the title row's original design.
  const content = (
    <>
      {/* Header */}
      <div className="relative flex items-center justify-between border-b border-outline px-4 py-3">
        {isPhone && (
          <div className="absolute left-1/2 top-2 -translate-x-1/2">
            <div className="h-1 w-10 rounded-full bg-outline" />
          </div>
        )}
        <DialogTitle className="text-lg font-semibold text-card-foreground">
          Cart &mdash;{' '}
          <span className={mode === 'buy' ? 'text-tertiary' : 'text-primary'}>
            {mode === 'buy' ? 'Buying' : 'Selling'}
          </span>
        </DialogTitle>
        <button
          onClick={onClose}
          className="rounded-full p-2 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Session tabs */}
      {(parkedSessions.length > 0 || canAddSession) && (
        <div className="flex items-center gap-2 overflow-x-auto border-b border-outline px-4 py-2 scrollbar-none">
          {/* Active session chip */}
          <div className="flex items-center gap-1">
            <Badge variant={mode === 'buy' ? 'tertiary' : 'default'} className="flex-shrink-0 px-3 py-1 text-xs">
              {session.label || `Session ${session.id.slice(0, 4)}`}
              <span className="ml-1 opacity-70">({session.items.length})</span>
            </Badge>
            <button
              onClick={() => startRenaming(session.id, session.label)}
              className="rounded p-1 text-muted-foreground hover:text-foreground"
              title="Rename session"
            >
              <Edit3 className="h-3 w-3" />
            </button>
          </div>

          {/* Parked session chips */}
          {parkedSessions.map((ps) => (
            <button
              key={ps.id}
              onClick={() => onSwitchSession(ps.id)}
              className="flex-shrink-0 rounded-full bg-muted px-3 py-1 text-xs font-medium text-muted-foreground hover:bg-outline/20 transition-colors"
            >
              {ps.label || `Session ${ps.id.slice(0, 4)}`}
              <span className="ml-1 opacity-70">({ps.items.length})</span>
            </button>
          ))}

          {/* New session button */}
          {canAddSession && (
            <button
              onClick={() => onStartNewSession(mode)}
              className="flex-shrink-0 rounded-full bg-muted p-1 text-muted-foreground hover:bg-outline/20 hover:text-foreground transition-colors"
              title="New session"
            >
              <Plus className="h-4 w-4" />
            </button>
          )}
        </div>
      )}

      {/* Rename input */}
      {renamingSessionId && (
        <div className="flex items-center gap-2 border-b border-outline px-4 py-2">
          <Input
            type="text"
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && commitRename()}
            onBlur={commitRename}
            placeholder="Customer name..."
            className="h-9 flex-1 text-sm"
            autoFocus
          />
        </div>
      )}

      {/* Item list */}
      <div className="flex-1 overflow-auto px-4 py-2">
        {session.items.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <p className="text-foreground">Cart is empty</p>
            <p className="mt-1 text-sm text-muted-foreground">
              Search for cards and add them to your cart
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {session.items.map((item) => (
              <div
                key={item.id}
                className="rounded-lg bg-muted px-3 py-2.5"
              >
                <div className="flex items-start justify-between gap-2">
                  {/* Left: card info */}
                  <div className="flex-1 min-w-0">
                    <p className="truncate text-sm font-medium text-foreground">
                      {item.cardName}
                    </p>
                    <p className="truncate text-xs text-muted-foreground">
                      {item.setName} &middot; #{item.cardNumber}
                    </p>
                    <div className="mt-1 flex items-center gap-1.5">
                      <span
                        className={cn(
                          'rounded px-1.5 py-0.5 text-xs font-medium',
                          CONDITION_BADGE_CLASS[item.condition] || 'badge-nm'
                        )}
                      >
                        {CONDITION_LABELS[item.condition] || item.condition.toUpperCase()}
                      </span>
                      {item.edition && (
                        <span className="rounded bg-amber-500/20 px-1.5 py-0.5 text-xs font-medium text-amber-600">
                          {getEditionFullName(item.edition)}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Right: price + remove */}
                  <div className="flex items-start gap-2">
                    <div className="text-right">
                      {editingItemId === item.id ? (
                        <div className="flex flex-col items-end gap-1">
                          <input
                            type="number"
                            value={editingPrice}
                            onChange={(e) => setEditingPrice(e.target.value)}
                            onBlur={commitEdit}
                            onKeyDown={(e) => e.key === 'Enter' && commitEdit()}
                            step="0.01"
                            min="0"
                            className="w-20 rounded border border-primary bg-background px-2 py-1 text-right text-sm text-foreground focus:outline-none"
                            autoFocus
                          />
                          <div className="flex gap-1">
                            {[-5, -1, 1, 5].map((delta) => (
                              <button
                                key={delta}
                                onClick={() =>
                                  adjustPrice(item.id, item.finalPrice, delta)
                                }
                                className="rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground hover:bg-outline/20"
                              >
                                {delta > 0 ? '+' : ''}${Math.abs(delta)}
                              </button>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <button
                          onClick={() => startEditing(item.id, item.finalPrice)}
                          className="text-right"
                        >
                          {item.finalPrice !== item.originalPrice && (
                            <span className="block text-xs text-muted-foreground line-through">
                              {formatPrice(item.originalPrice)}
                            </span>
                          )}
                          <span
                            className={cn(
                              'text-sm font-semibold',
                              mode === 'buy'
                                ? 'text-tertiary'
                                : 'text-primary'
                            )}
                          >
                            {formatPrice(item.finalPrice)}
                          </span>
                        </button>
                      )}
                    </div>
                    <button
                      onClick={() => onRemoveItem(item.id)}
                      className="rounded p-1 text-muted-foreground hover:bg-destructive/20 hover:text-destructive transition-colors"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      {session.items.length > 0 && (
        <div className="border-t border-outline px-4 py-3 pb-safe">
          {/* Total */}
          <div className="mb-3 flex items-center justify-between">
            <span className="text-sm text-muted-foreground">
              Total ({session.items.length} card
              {session.items.length !== 1 ? 's' : ''})
            </span>
            <span
              className={cn(
                'text-xl font-bold',
                mode === 'buy' ? 'text-tertiary' : 'text-primary'
              )}
            >
              {formatPrice(total)}
            </span>
          </div>

          {/* Action buttons */}
          <div className="flex gap-2">
            <Button onClick={onShowTotal} variant="secondary" className="flex-1">
              <Eye className="h-4 w-4" />
              Show Total
            </Button>
            <Button
              onClick={onCompleteSession}
              variant={mode === 'buy' ? 'tertiary' : 'default'}
              className="flex-1"
            >
              <CheckCircle className="h-4 w-4" />
              Complete
            </Button>
          </div>

          {/* Park + Clear row */}
          <div className="mt-2 flex gap-2">
            {parkedSessions.length < 3 && (
              <Button onClick={onParkSession} variant="secondary" size="sm" className="flex-1">
                Park Session
              </Button>
            )}
            <button
              onClick={onClearCart}
              className="flex items-center justify-center gap-1.5 rounded-full bg-destructive/10 px-3 py-2 text-xs font-medium text-destructive hover:bg-destructive/20 transition-colors"
            >
              <Trash2 className="h-3.5 w-3.5" />
              Clear
            </button>
          </div>
        </div>
      )}
    </>
  );

  if (isPhone) {
    return (
      <Sheet open onOpenChange={(open) => !open && onClose()}>
        <SheetContent className="mx-auto max-w-lg gap-0" showCloseButton={false}>
          {content}
        </SheetContent>
      </Sheet>
    );
  }

  // Tablet: centered modal
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className="flex max-h-[85vh] w-full max-w-lg flex-col gap-0 p-0"
        showCloseButton={false}
      >
        {content}
      </DialogContent>
    </Dialog>
  );
}
